import React, { useEffect, useState } from 'react';
import { Container, Navbar, Nav, Row, Col, Button, Form, Modal } from 'react-bootstrap';
import { StarFill, BucketFill, HouseDoorFill, Stars, TelephoneFill, EnvelopeFill, GeoAltFill, XCircleFill, Quote, SunFill, MoonFill, ShieldCheck, PatchCheckFill, HeartFill, ClockFill } from 'react-bootstrap-icons';
import emailjs from '@emailjs/browser';
import logo from './shannon-logo.png';
import './App.css';

const EMAILJS_SERVICE_ID = 'service_2bdjhys';
const EMAILJS_TEMPLATE_ID = 'template_a3yvthn';
const EMAILJS_PUBLIC_KEY = '1UVUIOAkXzm6spp_E';

function App() {
  const [expanded, setExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: 'Service Interested In',
    message: ''
  });
  const [isSending, setIsSending] = useState(false);
  const [modalState, setModalState] = useState({
    show: false,
    title: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNavClick = (event, targetId) => {
    event.preventDefault();
    setExpanded(false);

    const scrollToTarget = () => {
      const target = document.querySelector(targetId);
      if (!target) {
        return;
      }

      const navbar = document.querySelector('.navbar-custom');
      const navbarHeight = navbar ? navbar.offsetHeight : 0;
      const top = target.getBoundingClientRect().top + window.pageYOffset - navbarHeight - 8;

      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    };

    window.setTimeout(scrollToTarget, 60);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSending) {
      return;
    }

    const { name, email, service, message } = formData;
    setIsSending(true);

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        { name, email, service, message },
        EMAILJS_PUBLIC_KEY
      );
      setFormData({
        name: '',
        email: '',
        service: 'Service Interested In',
        message: ''
      });
      setModalState({
        show: true,
        title: 'Message sent',
        message: "Thanks! Your message has been sent. I'll get back to you soon."
      });
    } catch (error) {
      console.error('EmailJS send failed', error);
      setModalState({
        show: true,
        title: 'Something went wrong',
        message: 'Please try again in a moment or email me directly.'
      });
    } finally {
      setIsSending(false);
    }
  };
  
  // Handle Theme Change
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
      localStorage.setItem('theme', 'dark');
    } else {
      document.body.classList.remove('dark-mode');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);
  
  // Handle Scroll for Navbar styling
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, { threshold: 0.1 });

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach(el => observer.observe(el));

    return () => elements.forEach(el => observer.unobserve(el));
  }, []);

  return (
    <div className="App">
      {/* Top Bar - Info Strip */}
      <div className={`top-bar ${scrolled ? 'hidden' : ''} d-none d-md-block`}>
        <Container className="d-flex justify-content-between align-items-center">
          <div className="top-info">
            <span className="me-4"><GeoAltFill className="me-2" />Serving Bradford & Beyond</span>
          </div>
          <div className="top-contact">
             <a href="mailto:shanssparkle@gmail.com" className="me-4 text-white text-decoration-none">
               <EnvelopeFill className="me-2" />shanssparkle@gmail.com
             </a>
             <a href="tel:07507685441" className="text-white text-decoration-none fw-bold">
               <TelephoneFill className="me-2" />07507 685441
             </a>
          </div>
        </Container>
      </div>

      {/* Navigation */}
      <Navbar 
        expand="lg" 
        className={`navbar-custom sticky-top ${scrolled ? 'navbar-scrolled' : ''}`} 
        expanded={expanded} 
        onToggle={setExpanded}
      >
        <Container>
          <Navbar.Brand
            href="#home"
            className="d-flex align-items-center"
            onClick={(event) => handleNavClick(event, '#home')}
          >
            <div className="logo-wrapper">
                <img 
                  src={logo} 
                  alt="Shan's Cleaning" 
                  height="55" 
                  width="55"
                  className="d-inline-block align-top logo-img" 
                />
            </div>
            <div className="brand-text ms-2">
              <span className="brand-main">Shan's Sparkle</span>
              <span className="brand-sub d-block">Professional Cleaning</span>
            </div>
          </Navbar.Brand>
          
          <Navbar.Toggle aria-controls="basic-navbar-nav" className="hamburger-custom" />
          
          <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="ms-auto align-items-center">
                <Nav.Link href="#home" onClick={(event) => handleNavClick(event, '#home')}>Home</Nav.Link>
                <Nav.Link href="#about" onClick={(event) => handleNavClick(event, '#about')}>About</Nav.Link>
                <Nav.Link href="#services" onClick={(event) => handleNavClick(event, '#services')}>Services</Nav.Link>
                <Nav.Link href="#pricelist" onClick={(event) => handleNavClick(event, '#pricelist')}>Price List</Nav.Link>
                <Nav.Link href="#reviews" onClick={(event) => handleNavClick(event, '#reviews')}>Reviews</Nav.Link>

              
              {/* Theme Toggle Button */}
              <Button 
                variant="link" 
                className="nav-link border-0 bg-transparent p-2 mx-2"
                onClick={() => setDarkMode(!darkMode)}
                aria-label="Toggle Dark Mode"
                style={{ fontSize: '1.2rem', color: darkMode ? '#ff9ec1' : '#000000' }}
              >
                {darkMode ? <SunFill /> : <MoonFill />}
              </Button>

              <Button 
                href="#contact" 
                className="btn-nav ms-lg-2 mt-3 mt-lg-0" 
                onClick={(event) => handleNavClick(event, '#contact')}
              >
                Get a Quote
              </Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero Section */}
      <section id="home" className="hero-section d-flex align-items-center">
        <div className="hero-blob hero-blob-1"></div>
        <div className="hero-blob hero-blob-2"></div>
        
        <Container className="hero-content animate-fade-in">
          <Row className="justify-content-center">
            <Col md={10} lg={8}>
              <h1 className="hero-title">
                Experience the <span className="text-highlight">Sparkle</span> of a Clean Home
              </h1>
              <p className="hero-subtitle">
                Professional, reliable, and thorough cleaning services in Bradford.
                I treat your home with the care and attention it deserves, so you can relax.
              </p>
              <div className="d-flex justify-content-center gap-3">
                <Button href="#contact" className="btn-custom">Book a Clean</Button>
                <Button href="#services" className="btn-outline-custom">View Services</Button>
              </div>
            </Col>
          </Row>
        </Container>
        
        {/* Wave Divider Bottom */}
        <div className="wave-divider">
            <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="wave-fill-alt"></path>
            </svg>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-padding bg-light-alt">
        <Container>
          <div className="section-title-wrapper animate-on-scroll" style={{opacity: 0}}>
            <h2 className="section-title">Cleaning Services</h2>
            <div className="title-underline"></div>
            <p className="text-muted mt-3" style={{maxWidth: '600px', margin: '0 auto'}}>
              Tailored solutions to make your home shine.
            </p>
          </div>
          <Row>
            <Col md={4} className="mb-4 animate-on-scroll" style={{opacity: 0, animationDelay: '0.1s'}}>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <HouseDoorFill />
                </div>
                <h3 className="service-title">Standard Residential</h3>
                <p className="text-muted">Regular dusting, vacuuming, mopping, and bathroom/kitchen sanitation to keep your home fresh every week.</p>
              </div>
            </Col>
            <Col md={4} className="mb-4 animate-on-scroll" style={{opacity: 0, animationDelay: '0.2s'}}>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <Stars />
                </div>
                <h3 className="service-title">Deep Cleaning</h3>
                <p className="text-muted">A thorough, comprehensive clean. Ideal for spring cleaning or homes that haven't been professionally cleaned in a while.</p>
              </div>
            </Col>
            <Col md={4} className="mb-4 animate-on-scroll" style={{opacity: 0, animationDelay: '0.3s'}}>
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <BucketFill />
                </div>
                <h3 className="service-title">Move-In / Move-Out</h3>
                <p className="text-muted">Ensure you get your deposit back or walk into a pristine new home. We handle the heavy scrubbing for you.</p>
              </div>
            </Col>
          </Row>

          {/* Price List Section */}
          <div id="pricelist" className="price-list-container animate-on-scroll" style={{opacity: 0}}>
             <div className="text-center mb-4">
               <h3 className="section-title" style={{fontSize: '1.8rem'}}>2026 Price List</h3>
             </div>
             
             {/* Standard Clean */}
             <div className="price-row">
                <div>
                   <div className="price-service-name">Standard Clean</div>
                   <div className="price-service-desc">Weekly / Bi-weekly (2-4 hours)</div>
                </div>
                <div className="price-value">£50 - £80</div>
             </div>
             
             {/* Deep Clean */}
             <div className="price-row">
                <div>
                   <div className="price-service-name">Deep Clean</div>
                   <div className="price-service-desc">Thorough top-to-bottom (6-8 hours)</div>
                </div>
                <div className="price-value">£80 - £150</div>
             </div>

             {/* End of Tenancy */}
             <div className="price-row">
                <div>
                   <div className="price-service-name">End of Tenancy</div>
                   <div className="price-service-desc">Full deep clean (1-2 days)</div>
                </div>
                <div className="price-value">£150 - £300</div>
             </div>
          </div>

          {/* Services Not Included Section */}
          <div className="services-excluded-box animate-on-scroll" style={{opacity: 0}}>
            <h3 className="text-center h4 mb-4">Services Not Included</h3>
            <p className="text-center text-muted mb-4">
              To ensure safety and quality, please note the following exclusions:
            </p>
            <Row className="justify-content-center">
              <Col md={10} lg={9}>
                <Row>
                  <Col md={6}>
                    <ul className="list-unstyled">
                      <li className="mb-2 d-flex align-items-center text-muted">
                        <XCircleFill className="text-danger me-2 flex-shrink-0" /> 
                        <span>Working at heights</span>
                      </li>
                      <li className="mb-2 d-flex align-items-center text-muted">
                        <XCircleFill className="text-danger me-2 flex-shrink-0" /> 
                        <span>Washing and ironing</span>
                      </li>
                      <li className="mb-2 d-flex align-items-center text-muted">
                        <XCircleFill className="text-danger me-2 flex-shrink-0" /> 
                        <span>Biological / Pet waste removal</span>
                      </li>
                    </ul>
                  </Col>
                  <Col md={6}>
                    <ul className="list-unstyled">
                      <li className="mb-2 d-flex align-items-center text-muted">
                        <XCircleFill className="text-danger me-2 flex-shrink-0" /> 
                        <span>Heavy lifting (furniture moving)</span>
                      </li>
                      <li className="mb-2 d-flex align-items-center text-muted">
                        <XCircleFill className="text-danger me-2 flex-shrink-0" /> 
                        <span>Cleaning personal private items</span>
                      </li>
                      <li className="mb-2 d-flex align-items-center text-muted">
                        <XCircleFill className="text-danger me-2 flex-shrink-0" /> 
                        <span>Homes with illnesses (last 5 days)</span>
                      </li>
                    </ul>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        </Container>

        {/* Wave Divider to White */}
        <div className="wave-divider" style={{transform: 'rotate(180deg)', bottom: '-1px'}}>
            <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="wave-fill-white"></path>
            </svg>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-padding">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="mb-5 mb-md-0 animate-on-scroll" style={{opacity: 0}}>
              <Row className="g-4">
                <Col sm={6}>
                   <div className="skill-card">
                      <ShieldCheck className="skill-icon text-primary mb-3" size={40} />
                      <h5 className="fw-bold">Fully Insured</h5>
                      <p className="small text-muted mb-0">Complete liability coverage for your peace of mind.</p>
                   </div>
                </Col>
                <Col sm={6}>
                   <div className="skill-card">
                      <PatchCheckFill className="skill-icon text-primary mb-3" size={40} />
                      <h5 className="fw-bold">DBS Checked</h5>
                      <p className="small text-muted mb-0">Vetted and trustworthy service you can rely on.</p>
                   </div>
                </Col>
                <Col sm={6}>
                   <div className="skill-card">
                      <HeartFill className="skill-icon text-primary mb-3" size={40} />
                      <h5 className="fw-bold">Pet Friendly</h5>
                      <p className="small text-muted mb-0">Safe products and a caring attitude towards your pets.</p>
                   </div>
                </Col>
                <Col sm={6}>
                   <div className="skill-card">
                      <ClockFill className="skill-icon text-primary mb-3" size={40} />
                      <h5 className="fw-bold">Reliable</h5>
                      <p className="small text-muted mb-0">Consistent, punctual, and communicative service.</p>
                   </div>
                </Col>
              </Row>
            </Col>
            <Col md={6} className="ps-md-5 animate-on-scroll" style={{opacity: 0, animationDelay: '0.2s'}}>
              <h2 className="section-title mb-4">Meet Shannon</h2>
              <p className="lead text-primary fw-bold">Owner & Operator of Shan's Sparkle</p>
              <p className="text-muted">
                Hi, I'm Shannon Moss. I founded Shan's Sparkle with a simple mission: to give my neighbors the peace of mind that comes with a beautifully clean home.
              </p>
              <p className="text-muted">
                As a solo operator, I take personal pride in every job. You aren't just hiring a company; you're hiring me. This means consistent quality, trustworthy service, and a friendly face you can rely on. I treat every space as if it were my own, using safe and effective products to ensure a healthy environment.
              </p>
              <Button href="#contact" className="btn-custom mt-3">Get in Touch</Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Testimonials Section */}
      <section id="reviews" className="section-padding bg-light-alt">
        {/* Wave Divider Top */}
        <div className="wave-divider" style={{top: -1, bottom: 'auto'}}>
            <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="wave-fill-white"></path>
            </svg>
        </div>

        <Container>
          <div className="section-title-wrapper animate-on-scroll" style={{opacity: 0}}>
             <h2 className="section-title">Happy Clients</h2>
             <div className="title-underline"></div>
          </div>
         
          <Row>
            <Col md={4} className="mb-4 animate-on-scroll" style={{opacity: 0, animationDelay: '0.1s'}}>
              <div className="testimonial-card">
                <Quote className="quote-icon" />
                <div className="stars">
                  <StarFill /><StarFill /><StarFill /><StarFill /><StarFill />
                </div>
                <p className="testimonial-text">"Shannon is absolutely wonderful! She is thorough, reliable, and always leaves my house smelling amazing. Highly recommend!"</p>
                <div className="fw-bold text-primary">- Sarah J.</div>
              </div>
            </Col>
            <Col md={4} className="mb-4 animate-on-scroll" style={{opacity: 0, animationDelay: '0.2s'}}>
              <div className="testimonial-card">
                <Quote className="quote-icon" />
                <div className="stars">
                  <StarFill /><StarFill /><StarFill /><StarFill /><StarFill />
                </div>
                <p className="testimonial-text">"The best cleaning service I've ever used. Shannon pays attention to details that others miss. My kitchen has never looked this good."</p>
                <div className="fw-bold text-primary">- Mike T.</div>
              </div>
            </Col>
            <Col md={4} className="mb-4 animate-on-scroll" style={{opacity: 0, animationDelay: '0.3s'}}>
              <div className="testimonial-card">
                <Quote className="quote-icon" />
                <div className="stars">
                  <StarFill /><StarFill /><StarFill /><StarFill /><StarFill />
                </div>
                <p className="testimonial-text">"I hired Shan's Cleaning for a move-out clean, and the landlord was impressed. Full deposit back! Thank you, Shannon!"</p>
                <div className="fw-bold text-primary">- Emily R.</div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-primary-soft">
         {/* Wave Divider Top */}
         <div className="wave-divider" style={{top: -1, bottom: 'auto'}}>
            <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="wave-fill-alt"></path>
            </svg>
        </div>

        <Container className="animate-on-scroll" style={{opacity: 0}}>
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="contact-card">
                <Row className="g-0">
                  <Col md={5} className="contact-info-side d-flex flex-column justify-content-center">
                    <h3 className="text-white mb-4">Let's Connect</h3>
                    <p className="text-white mb-5">Ready for a sparkling home? Fill out the form or reach out directly.</p>
                    
                    <div className="d-flex align-items-center mb-4">
                      <div className="bg-white bg-opacity-25 p-2 rounded-circle me-3">
                        <TelephoneFill className="text-white fs-5" />
                      </div>
                      <span className="text-white fw-bold">07507 685441</span>
                    </div>
                    
                    <div className="d-flex align-items-center mb-4">
                      <div className="bg-white bg-opacity-25 p-2 rounded-circle me-3">
                        <EnvelopeFill className="text-white fs-5" />
                      </div>
                      <span className="text-white fw-bold">shanssparkle@gmail.com</span>
                    </div>
                    
                    <div className="d-flex align-items-center">
                      <div className="bg-white bg-opacity-25 p-2 rounded-circle me-3">
                        <GeoAltFill className="text-white fs-5" />
                      </div>
                      <span className="text-white">Serving <strong>Bradford</strong> & Surrounding Areas</span>
                    </div>
                  </Col>
                  
                  <Col md={7} className="contact-form-side">
                    <h4 className="mb-4 text-dark">Send a Message</h4>
                    <Form onSubmit={handleSubmit}>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Control 
                              type="text" 
                              name="name"
                              placeholder="Your Name" 
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Control 
                              type="email" 
                              name="email"
                              placeholder="Email Address" 
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Form.Group className="mb-3">
                        <Form.Select 
                          name="service"
                          value={formData.service}
                          onChange={handleInputChange}
                          className="text-muted"
                        >
                          <option>Service Interested In</option>
                          <option>Standard Cleaning</option>
                          <option>Deep Cleaning</option>
                          <option>Move-In/Out</option>
                        </Form.Select>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Control 
                          as="textarea" 
                          rows={4} 
                          name="message"
                          placeholder="How can I help you?" 
                          value={formData.message}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                      
                      <Button className="btn-custom w-100 mt-2" type="submit" disabled={isSending}>
                        {isSending ? 'Sending...' : 'Send Message'}
                      </Button>
                    </Form>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <Modal
        show={modalState.show}
        onHide={() => setModalState(prev => ({ ...prev, show: false }))}
        centered
        contentClassName="contact-modal-content"
      >
        <Modal.Header closeButton>
          <Modal.Title>{modalState.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalState.message}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setModalState(prev => ({ ...prev, show: false }))}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Footer */}
      <footer className="footer text-center position-relative">
         {/* Wave Divider Top */}
         <div className="wave-divider" style={{top: -1, bottom: 'auto'}}>
            <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="wave-fill-footer"></path>
            </svg>
        </div>
        <Container className="position-relative z-1">
          <h4 className="text-white mb-3">Shan's Sparkle</h4>
          <p className="mb-4 opacity-75">Making homes happier, one clean at a time.</p>
          <div className="opacity-50 small">
            &copy; {new Date().getFullYear()} Shan's Cleaning. All Rights Reserved.
          </div>
        </Container>
      </footer>
    </div>
  );
}

export default App;
